import { useState, useMemo, useEffect } from 'react';
import { EffectiveDateInput } from '../components/effectiveDateInput';
import { Button } from '@/components/ui/button';
import { toDateString, formatDate } from '@/lib/utils/dates';
import {
  LocationSelect as Select,
  AutoFilled,
} from '@/lib/pages/forms/components';
import type { Policy } from '../../../heyapi';
import {
  glacierPolicyApiCreateMidtermTransactionMutation,
  glacierPolicyApiCreatePolicyPendingCancellationTransactionMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import {
  CancellationReasonEnum,
  CancelTypeEnum,
  TransactionType,
} from '@/lib/heyapi/types.gen';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { useParams, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';

// DUE TO NON PAYMENT (Print Cancellation Endorsement)s
// REQUESTED BY INSURED (Print Cancellation Endorsement)
// FAILURE TO RENEW (Print Good Bye Letter)
// REQUESTED BY COMPANY (Available to Underwriter role) (Print Cancellation Endorsement)

const baseReasonOptions = [
  {
    label: 'DUE TO NON PAYMENT',
    value: CancellationReasonEnum.DUE_TO_NON_PAYMENT,
  },
  {
    label: 'REQUESTED BY INSURED',
    value: CancellationReasonEnum.REQUESTED_BY_INSURED,
  },
  { label: 'FAILURE TO RENEW', value: CancellationReasonEnum.FAILURE_TO_RENEW },
  {
    label: 'REQUESTED BY COMPANY',
    value: CancellationReasonEnum.REQUESTED_BY_COMPANY,
  },
];

function convertDateToString(d: Date): Date {
  // Convert the date to a string in the format YYYY-MM-DD
  // We show return type as Date because there is a bug in
  // either the formDataBodySerializer for react query or in ninja schema,
  // this is just a workaround/hack
  return d.toISOString().split('T')[0] as unknown as Date;
}

export function CancellationForm({ policy }: { policy: Policy | undefined }) {
  type YesNoEnumForCheckbox = 'yes' | 'no';
  const YesNoEnumForCheckbox = {
    NO: 'no',
    YES: 'yes',
  } as const;

  const [newEffectiveDate, setNewEffectiveDate] = useState('');
  const [reason, setReason] = useState<CancellationReasonEnum | ''>('');
  const [type, setType] = useState<CancelTypeEnum | ''>('');
  const [cancellationRequired, setCancellationRequired] =
    useState<YesNoEnumForCheckbox>(YesNoEnumForCheckbox.NO);
  const [noticeGenerationDate, setNoticeGenerationDate] = useState('');
  const [cancellationProcessDate, setCancellationProcessDate] = useState('');
  const [uploadedDocument, setUploadedDocument] = useState<File | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedDocument(e.target.files[0]);
    }
  };
  const { id } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  function calcualteMinEffectiveDate(policy: Policy | undefined): Date {
    // Determine the minimum effective date for cancellation

    let policyEffectiveDate = policy?.details?.policyEffectiveDate;
    let latestTransactionEffectiveDate =
      policy?.latestTransaction.effectiveDate;

    // Set the minimum effective date for cancellation
    // policy?.details?.policyEffectiveDate OR policy?.latestTransaction.effectiveDate
    // which ever is the latest
    let cancelMinEffectiveDate = policyEffectiveDate;

    if (
      latestTransactionEffectiveDate &&
      policyEffectiveDate &&
      latestTransactionEffectiveDate > policyEffectiveDate
    ) {
      cancelMinEffectiveDate = latestTransactionEffectiveDate;
    }

    // Turn the date into a string and replace the hyphens with slashes
    // to make it compatible with the Date constructor
    let cancelMinEffectiveString = '' + cancelMinEffectiveDate;
    cancelMinEffectiveString = cancelMinEffectiveString.replace(/-/g, '/');

    // Convert the string back to a Date object
    return new Date(cancelMinEffectiveString);
  }

  const createMidterm = useMutation({
    ...glacierPolicyApiCreateMidtermTransactionMutation(),
    onError: (error) => {
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
            <code className="text-white">{error as string}</code>
          </pre>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: qk });
      navigate(`/policy/${id}/pricing-preview`);

      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">
              Creating Cancellation Transaction
            </code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/pricing-preview`;
      }, 500);
    },
  });

  const createMidtermWithCancellationNotice = useMutation({
    ...glacierPolicyApiCreatePolicyPendingCancellationTransactionMutation(),
    onError: (_) => {
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
            <code className="text-white">
              There was an error, please try again later
            </code>
          </pre>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: qk });
      navigate(`/policy/${id}/pricing-preview`);

      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">
              Creating Cancellation Transaction
            </code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/pricing-preview`;
      }, 500);
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEffectiveDate(e.target.value);
  };

  const handleReasonChange = (value: string) => {
    setReason(value as CancellationReasonEnum);
  };

  const handleTypeChange = (value: string) => {
    setType(value as CancelTypeEnum);
  };

  const typeOptions = useMemo(() => {
    // Only allow FLAT_CANCEL if the policy effective date is the same as the latest transaction effective date
    // NOTE: This is a temporary until we have support for out of sequence transactions

    let optionsForInput: { label: string; value: CancelTypeEnum }[] = [];

    if (reason === CancellationReasonEnum.DUE_TO_NON_PAYMENT) {
      // FLAT CANCEL - Assume Effective is the Effective date of the Policy
      // PRO RATA - Ask for Effective Date
      optionsForInput = [
        { label: 'FLAT CANCEL', value: CancelTypeEnum.FLAT_CANCEL },
        { label: 'PRO RATA', value: CancelTypeEnum.PRO_RATA },
      ];
    }

    if (reason === CancellationReasonEnum.REQUESTED_BY_INSURED) {
      // FLAT CANCEL - Assume Effective is the Effective date of the Policy
      // PRO RATA - Ask for Effective Date
      // SHORT - Ask for Effective Date

      optionsForInput = [
        { label: 'FLAT CANCEL', value: CancelTypeEnum.FLAT_CANCEL },
        { label: 'PRO RATA', value: CancelTypeEnum.PRO_RATA },
        { label: 'SHORT', value: CancelTypeEnum.SHORT },
      ];
    }

    if (reason === CancellationReasonEnum.FAILURE_TO_RENEW) {
      optionsForInput = [
        { label: 'FLAT CANCEL', value: CancelTypeEnum.FLAT_CANCEL },
      ];
    }

    if (reason === CancellationReasonEnum.REQUESTED_BY_COMPANY) {
      // FLAT CANCEL - Assume Effective is the Effective date of the Policy
      // PRO RATA - Ask for Effective Date
      // SHORT - Ask for Effective Date

      optionsForInput = [
        { label: 'FLAT CANCEL', value: CancelTypeEnum.FLAT_CANCEL },
        { label: 'PRO RATA', value: CancelTypeEnum.PRO_RATA },
        { label: 'SHORT', value: CancelTypeEnum.SHORT },
      ];
    }

    // IF canFlatCancel is false, then remove FLAT_CANCEL from the options
    let canFlatCancel =
      policy?.details?.policyEffectiveDate ===
      policy?.latestTransaction.effectiveDate;
    if (!canFlatCancel) {
      optionsForInput = optionsForInput.filter(
        (option) => option.value !== CancelTypeEnum.FLAT_CANCEL
      );
    }

    return optionsForInput;
  }, [reason]);

  const reasonOptions = useMemo(() => {
    // IF policyEffectiveDate != latestTransactionEffectiveDate THEN remove FAILURE TO RENEW as option
    return baseReasonOptions.filter((option) => {
      if (
        option.value === CancellationReasonEnum.FAILURE_TO_RENEW &&
        policy?.details?.policyEffectiveDate !==
          policy?.latestTransaction.effectiveDate
      ) {
        return false;
      }
      return true;
    });
  }, [policy]);

  useEffect(() => {
    const minEffectiveDate = calcualteMinEffectiveDate(policy);

    if (type === CancelTypeEnum.SHORT) {
      setNewEffectiveDate('');
    }

    if (type === CancelTypeEnum.FLAT_CANCEL) {
      setNewEffectiveDate(toDateString(minEffectiveDate));
    }

    if (type === CancelTypeEnum.PRO_RATA) {
      setNewEffectiveDate('');
    }
  }, [type]);

  const form = useForm({});

  const { handleSubmit: hookFormSubmit } = form;

  const onSubmit = () => {
    if (cancellationRequired === YesNoEnumForCheckbox.YES) {
      if (
        !uploadedDocument ||
        !cancellationProcessDate ||
        !noticeGenerationDate ||
        !reason ||
        !type
      ) {
        toast({
          title: 'Error',
          description: (
            <pre className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
              <code className="whitespace-pre-wrap text-white">
                Please make sure to fill all the fields and upload the file for
                Notice of Cancellation
              </code>
            </pre>
          ),
        });
        return;
      }

      createMidtermWithCancellationNotice.mutate({
        body: {
          effective_date: convertDateToString(new Date(newEffectiveDate)),
          cancel_type: type,
          cancellation_reason: reason,
          cancellation_process_date: convertDateToString(
            new Date(cancellationProcessDate)
          ),
          notice_generation_date: convertDateToString(
            new Date(noticeGenerationDate)
          ),
          file: uploadedDocument,
        },
        path: {
          account_id: id!,
        },
      });
    } else {
      createMidterm.mutate({
        body: {
          effective_date: new Date(newEffectiveDate),
          transaction_type: TransactionType.POLICY_CANCELLED,
          metadata: {
            cancellation_reason: reason || null,
            cancel_type: type || null,
          },
        },
        path: {
          account_id: id!,
        },
      });
    }
    // Perform any additional actions with the effectiveDate value
  };

  const effectiveDateMinimum = toDateString(calcualteMinEffectiveDate(policy));

  return (
    <Form {...form}>
      <form onSubmit={hookFormSubmit(onSubmit)} className="flex flex-col gap-8">
        <div className="flex items-center gap-4">
          <label className="w-1/3 font-semibold leading-[1.5rem]">
            Notice of cancellation required?
          </label>
          <RadioGroup
            onValueChange={(e) => {
              setCancellationRequired(e as YesNoEnumForCheckbox);
            }}
            defaultValue={YesNoEnumForCheckbox.NO}
            value={cancellationRequired}
            className="flex space-x-2"
          >
            <div className="space-x-1">
              <RadioGroupItem value={YesNoEnumForCheckbox.YES} />
              <label className="font-normal capitalize">Yes</label>
            </div>
            <div className="space-x-1">
              <RadioGroupItem value={YesNoEnumForCheckbox.NO} />
              <label className="font-normal capitalize">No</label>
            </div>
          </RadioGroup>
        </div>
        {cancellationRequired === YesNoEnumForCheckbox.YES && (
          <>
            <EffectiveDateInput
              value={noticeGenerationDate}
              onChange={(e) => setNoticeGenerationDate(e.target.value)}
              orientation="horizontal"
              label="Notice generation date"
              min={effectiveDateMinimum ? effectiveDateMinimum : undefined}
              max={
                policy?.details?.policyExpirationDate
                  ? toDateString(policy?.details?.policyExpirationDate)
                  : undefined
              }
              required={cancellationRequired === YesNoEnumForCheckbox.YES}
              stretch
            />
            <EffectiveDateInput
              value={cancellationProcessDate}
              onChange={(e) => setCancellationProcessDate(e.target.value)}
              orientation="horizontal"
              label="Cancellation process date"
              min={effectiveDateMinimum ? effectiveDateMinimum : undefined}
              max={
                policy?.details?.policyExpirationDate
                  ? toDateString(policy?.details?.policyExpirationDate)
                  : undefined
              }
              required={cancellationRequired === YesNoEnumForCheckbox.YES}
              stretch
            />
            <div className="flex flex-col gap-4">
              <label
                htmlFor="file-upload"
                className="font-semibold leading-[1.5rem]"
              >
                Upload Notice of Cancellation document{' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                type="file"
                id="file-upload"
                name="file-upload"
                className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
                onChange={handleFileUpload}
              />
            </div>
            <div className="my-2 border-t border-gray-300"></div>
          </>
        )}
        <Select
          value={reason || ''}
          options={reasonOptions}
          onChange={handleReasonChange}
          name="reason"
          placeholder="Select"
          label="Reason"
          orientation="horizontal"
          required
          ignoreReadMode
          stretch
        />
        <Select
          value={type || ''}
          options={typeOptions}
          onChange={handleTypeChange}
          name="type"
          placeholder="Select"
          label="Type"
          orientation="horizontal"
          required
          ignoreReadMode
          stretch
          disabled={!reason}
        />

        <div
          className={`${type === CancelTypeEnum.SHORT || type === CancelTypeEnum.PRO_RATA ? 'hidden' : ''}`}
        >
          <AutoFilled
            label="Effective date"
            value={formatDate(newEffectiveDate)}
            stretch
          />
        </div>

        <div
          className={`${type === CancelTypeEnum.SHORT || type === CancelTypeEnum.PRO_RATA ? '' : 'hidden'}`}
        >
          <EffectiveDateInput
            value={newEffectiveDate}
            onChange={handleInputChange}
            orientation="horizontal"
            label="Effective date"
            min={effectiveDateMinimum ? effectiveDateMinimum : undefined}
            max={
              policy?.details?.policyExpirationDate
                ? toDateString(policy?.details?.policyExpirationDate)
                : undefined
            }
            required
            stretch
            disabled={
              !(
                type === CancelTypeEnum.SHORT ||
                type === CancelTypeEnum.PRO_RATA
              )
            }
          />
        </div>

        <Button type="submit">Confirm</Button>
      </form>
    </Form>
  );
}
